import React, { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { LOCATION_STATUS_CONST } from "@/config/constants";
import { locationManagementService } from "../services/locationManagementService";

type LocationHistoryEntry = {
  date?: string;
  summary_date?: string;
  user?: string;
  value?: string | number;
};

type LocationWithHistory = {
  fk_location_id?: string | number;
  location_id?: string | number;
  location_status_history?: LocationHistoryEntry[];
  [key: string]: unknown;
};

type HistoryResponse = {
  data?: {
    data?: unknown;
  };
};

type LocationEditHistoryModalProps = {
  brandId?: string | number;
  open: boolean;
  onClose: () => void;
  location?: LocationWithHistory;
};

const STATUS_STYLES = {
  [LOCATION_STATUS_CONST.OPEN.LABEL]: {
    backgroundColor: "var(--status-success-tint)",
    color: "var(--status-success)",
  },
  [LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.LABEL]: {
    backgroundColor: "var(--status-warning-tint)",
    color: "var(--status-warning)",
  },
  [LOCATION_STATUS_CONST.CLOSED.LABEL]: {
    backgroundColor: "var(--status-error-tint)",
    color: "var(--status-error)",
  },
};

const getHistoryFromResponse = (
  response?: HistoryResponse
): LocationHistoryEntry[] => {
  const history = response?.data?.data || [];

  return Array.isArray(history) ? history : [];
};

const getHistoryFromLocation = (
  location?: LocationWithHistory
): LocationHistoryEntry[] | null => {
  if (!location || !Array.isArray(location.location_status_history)) {
    return null;
  }

  return location.location_status_history;
};

const getDisplayStatus = (status?: string | number) =>
  LOCATION_STATUS_CONST.LABEL_BY_ID[status] ||
  status ||
  LOCATION_STATUS_CONST.DEFAULT;

const StatusChip = ({ status }: { status?: string | number }) => {
  const statusText = getDisplayStatus(status);
  const statusStyle =
    STATUS_STYLES[statusText] || STATUS_STYLES[LOCATION_STATUS_CONST.DEFAULT];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 26,
        borderRadius: "var(--radius-round)",
        px: 1,
        py: 0.5,
        color: statusStyle.color,
        backgroundColor: statusStyle.backgroundColor,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
      }}
    >
      {statusText}
    </Box>
  );
};

const HeaderCell = ({ children }: { children: ReactNode }) => (
  <TableCell
    sx={{
      backgroundColor: "var(--neutral-100)",
      borderBottom: 0,
      color: "var(--surface-dark)",
      fontSize: 14,
      fontWeight: 700,
      height: 46,
      lineHeight: 1.5,
      px: 1.5,
      py: 0,
      top: 0,
      zIndex: 1,
    }}
  >
    {children}
  </TableCell>
);

const LocationEditHistoryModal = ({
  brandId,
  open,
  onClose,
  location,
}: LocationEditHistoryModalProps) => {
  const [historyEntries, setHistoryEntries] = useState<
    LocationHistoryEntry[] | null
  >(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const locationId = location?.fk_location_id || location?.location_id;

  useEffect(() => {
    if (!open) return;

    let isActive = true;

    const loadHistory = async () => {
      if (!brandId || !locationId) {
        setHistoryEntries(null);
        return;
      }

      setHistoryEntries(null);
      setIsLoadingHistory(true);
      try {
        const response =
          await locationManagementService.getLocationStatusHistory(
            brandId,
            locationId
          );
        if (isActive) setHistoryEntries(getHistoryFromResponse(response));
      } catch (_e) {
        if (isActive) setHistoryEntries(null);
      } finally {
        if (isActive) setIsLoadingHistory(false);
      }
    };

    loadHistory();

    return () => {
      isActive = false;
    };
  }, [brandId, locationId, open]);

  const rows = useMemo(
    () =>
      (historyEntries || []).map((entry, index) => ({
        id: `${entry.summary_date || "history"}-${index}`,
        date: entry.date,
        participantName: entry.user,
        status: getDisplayStatus(entry.value),
      })),
    [historyEntries]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor:
              "color-mix(in srgb, var(--palette-common-black) 35%, transparent)",
          },
        },
        paper: {
          sx: {
            borderRadius: "var(--radius-container)",
            boxShadow: "var(--shadow-overlay)",
            maxWidth: 698,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.125, mb: 3 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "var(--radius-container)",
              backgroundColor: "var(--primary-tint)",
              display: "grid",
              flexShrink: 0,
              placeItems: "center",
            }}
          >
            <HistoryOutlinedIcon
              sx={{ color: "var(--primary-base)", fontSize: 24 }}
            />
          </Box>
          <Typography
            sx={{
              color: "var(--palette-text-primary)",
              flex: 1,
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.5,
            }}
          >
            View Edit History
          </Typography>
          <IconButton
            aria-label="Close edit history"
            onClick={onClose}
            sx={{
              borderRadius: "var(--radius-control)",
              color: "var(--palette-text-primary)",
              height: 46,
              ml: "auto",
              width: 46,
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>

        <TableContainer sx={{ maxHeight: 276, overflowY: "auto" }}>
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <HeaderCell>Date</HeaderCell>
                <HeaderCell>Updated By</HeaderCell>
                <HeaderCell>Status</HeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingHistory ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 4, textAlign: "center" }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor:
                        index % 2
                          ? "var(--neutral-50)"
                          : "var(--neutral-white)",
                    }}
                  >
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--neutral-200)",
                        color: "var(--surface-dark)",
                        fontSize: 14,
                        height: 46,
                        lineHeight: 1.5,
                        overflow: "hidden",
                        px: 1.5,
                        py: 0,
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.date || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--neutral-200)",
                        color: "var(--palette-text-primary)",
                        fontSize: 14,
                        height: 46,
                        lineHeight: 1.5,
                        overflow: "hidden",
                        px: 1.5,
                        py: 0,
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.participantName || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid var(--neutral-200)",
                        height: 46,
                        px: 1.5,
                        py: 0,
                      }}
                    >
                      <StatusChip status={row.status} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{
                      color: "var(--neutral-600)",
                      fontSize: 16,
                      py: 3,
                    }}
                  >
                    No edit history available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export const getLocationEditHistorySummary = (
  location: LocationWithHistory = {}
) => {
  const [historyEntry] = getHistoryFromLocation(location) || [];

  if (!historyEntry) {
    return "No edit history available";
  }

  return [historyEntry.user, historyEntry.summary_date]
    .filter(Boolean)
    .join(" ");
};

export default LocationEditHistoryModal;
